/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Context, Sampler, SamplingDecision, SamplingResult, trace } from "@opentelemetry/api";
import { AlwaysOffSampler, AlwaysOnSampler, ParentBasedSampler } from "@opentelemetry/sdk-trace-base";
import { getFw4Tag } from "../../export/SpanEmbedder";

// ============================================================================
/* eslint-disable @typescript-eslint/no-deprecated */
/**
 * Sampler that respects the ignore flag of the FW4 tag. If no FW4 tag exists the span
 * is sampled.
 */
export class DtFw4TagSampler implements Sampler {
	shouldSample(context: Context): SamplingResult {
		const parentSpanContext = trace.getSpanContext(context);
		const fw4Tag = getFw4Tag(parentSpanContext?.traceState);
		return {
			decision: fw4Tag?.isIgnored ? SamplingDecision.NOT_RECORD : SamplingDecision.RECORD_AND_SAMPLED
		};
	}

	toString(): string {
		return "DtFw4TagSampler";
	}
}

// ----------------------------------------------------------------------------
export class DtSampler extends ParentBasedSampler {
	constructor() {
		const fw4TagSampler = new DtFw4TagSampler();
		super({
			root: new AlwaysOnSampler(),
			remoteParentNotSampled: fw4TagSampler,
			remoteParentSampled: fw4TagSampler,
			localParentNotSampled: new AlwaysOffSampler(),
			localParentSampled: new AlwaysOnSampler()
		});
	}

	override toString(): string {
		return "DtSampler";
	}
}
